"use client";

import type {
  ImageContent,
  PlaceholderValues,
  Rating,
} from "@homeinstead/schemas";
import type { FormRegularStep } from "./form-types";

import { cn } from "@homeinstead/ui";
import Stepper from "@homeinstead/ui/components/Stepper";
import { memo } from "react";
import { ContinueButton } from "./ContinueButton";
import { toContentAlignment } from "./form-step";
import { getInfoPanelHeroStyles } from "./get-info-panel-hero-styles";
import { LeadFormInfoPanel } from "./LeadFormInfoPanel";
import { LeadFormStep } from "./LeadFormStep";
import { LeadFormVisibleItems } from "./LeadFormVisibleItems";
import {
  defaultLeadFormStyleClasses,
  getBreakpointKey,
} from "./lead-form-styles";
import { useLeadFormStepKeyboardAdvance } from "./use-lead-form-step-keyboard-advance";

type StepperProps = {
  currentStep: number;
  totalSteps: number;
  handleBack: () => void;
  showBackButton: boolean;
  showStepper: boolean;
};

export type LeadFormStepItemProps = {
  step: FormRegularStep;
  isActive: boolean;
  /** True when the LeadFormStepWrapper is in hero mode (has negative margins).
   * When true, stepContentHeroHorizontalPadding is applied to compensate. */
  isStepWrapperHeroMode: boolean;
  errors: Record<string, string[]>;
  isPending: boolean;
  isLastStep: boolean;
  officeRating?: Omit<Rating, "_type">;
  phoneNumber?: string;
  placeholderValues?: PlaceholderValues;
  postalLocationValues: { postalCode: string; location: string };
  heroImageLarge?: ImageContent | null;
  heroImageSmall?: ImageContent | null;
  onContinue: () => void;
  onFieldChange: (name: string, value: string) => void;
  onPostalLocationChange: (values: {
    postalCode: string;
    location: string;
  }) => void;
  onValidationError: (errors: Record<string, string[]>) => void;
  stepperProps: StepperProps;
  /**
   * Analytics `user_selection` for the step's input(s), derived from all
   * persisted field values on the step. Forwarded to a step-level `formButton`
   * so the unified "Lead Form Step" event carries the full selection when inputs
   * do not own their own Continue button. Field-keyed object; multi-select
   * fields are arrays.
   */
  stepUserSelection?: Record<string, string | string[]>;
};

function LeadFormStepItemInner({
  step,
  isActive,
  isStepWrapperHeroMode,
  errors,
  isPending,
  isLastStep,
  officeRating,
  phoneNumber,
  placeholderValues,
  postalLocationValues,
  heroImageLarge,
  heroImageSmall,
  onContinue,
  onFieldChange,
  onPostalLocationChange,
  onValidationError,
  stepperProps,
  stepUserSelection,
}: LeadFormStepItemProps) {
  const stepAnalyticsName = step.metricsLabel ?? step.id;

  const stepHasFormButton =
    step.inputs?.some((input) => input._type === "formButton") ?? false;
  // multiSelectInput renders its own Continue button internally,
  // so treat it the same as an explicit formButton to avoid a duplicate.
  const stepHasExplicitButton =
    step.inputs?.some(
      (input) =>
        input._type === "formButton" || input._type === "multiSelectInput",
    ) ?? false;

  const { stepHandleContinue, stepHasAutoAdvance, stepVisibleItems } =
    useLeadFormStepKeyboardAdvance({
      isActive,
      isPending,
      onContinue,
      onValidationError,
      postalLocationValues,
      step,
      stepAnalyticsName,
      stepUserSelection,
    });

  const stepHasInfoPanel = Boolean(step.infoPanel);
  const heroBreakpoint = getBreakpointKey(step.infoPanel?.showFromBreakpoint);
  const isStepHeroMode = step.infoPanel?.mode === "hero";
  const heroVariantStyles = isStepHeroMode
    ? getInfoPanelHeroStyles(step.infoPanel)
    : undefined;
  const { textColorClass, continueButtonVariant } = heroVariantStyles ?? {};
  const stepFlexRowClass = stepHasInfoPanel
    ? cn(
        isStepHeroMode
          ? defaultLeadFormStyleClasses.flexRowBreakpointHero[heroBreakpoint]
          : defaultLeadFormStyleClasses.flexRowBreakpoint[heroBreakpoint],
        isStepHeroMode
          ? defaultLeadFormStyleClasses.buttonStackHeroGrid
          : defaultLeadFormStyleClasses.buttonStackItemsGrid,
      )
    : undefined;
  return (
    <LeadFormStep
      className={cn(
        defaultLeadFormStyleClasses.stepBase,
        { [defaultLeadFormStyleClasses.stepGap]: !isStepHeroMode },
        stepFlexRowClass,
      )}
      contentAlignment={toContentAlignment(step)}
      isVisible={isActive}
      verticalPadding="none"
    >
      <div
        className={cn(
          defaultLeadFormStyleClasses.stepContentBase,
          {
            [defaultLeadFormStyleClasses.stepContentHeroHorizontalPadding]:
              isStepWrapperHeroMode,
            [defaultLeadFormStyleClasses.stepContentHeroPadding]:
              isStepHeroMode,
          },
          isStepHeroMode ? textColorClass : undefined,
        )}
      >
        {isStepHeroMode && stepperProps.showStepper ? (
          <Stepper
            {...stepperProps}
            className={cn(
              defaultLeadFormStyleClasses.stepperBase,
              defaultLeadFormStyleClasses.stepperPaddingTop,
            )}
            labelRowClassName={
              defaultLeadFormStyleClasses.stepperLabelRowSpacing
            }
            textColorClass={textColorClass}
          />
        ) : null}
        <LeadFormVisibleItems
          contentMaxWidth={step.contentMaxWidth}
          errors={isActive ? errors : undefined}
          heroVariantStyles={heroVariantStyles}
          hideContinueButton={stepHasFormButton}
          isSubmitting={isActive && isPending && isLastStep}
          officeRating={officeRating}
          onContinue={stepHandleContinue}
          onFieldChange={onFieldChange}
          onPostalLocationChange={onPostalLocationChange}
          phoneNumber={phoneNumber}
          placeholderValues={placeholderValues}
          stepId={step.id}
          stepName={stepAnalyticsName}
          stepUserSelection={stepUserSelection}
          visibleItems={stepVisibleItems}
        />
        {!stepHasExplicitButton && !stepHasAutoAdvance ? (
          <div className={defaultLeadFormStyleClasses.continueButtonWrapper}>
            <ContinueButton
              analyticsStepName={stepAnalyticsName}
              analyticsUserSelection={stepUserSelection}
              disabled={isActive && isPending}
              onContinue={stepHandleContinue}
              variant={continueButtonVariant}
            />
          </div>
        ) : null}
      </div>

      {step.infoPanel ? (
        <LeadFormInfoPanel
          heroImageLarge={heroImageLarge}
          heroImageSmall={heroImageSmall}
          infoPanel={step.infoPanel}
          placeholderValues={placeholderValues}
        />
      ) : null}
    </LeadFormStep>
  );
}

export const LeadFormStepItem = memo(LeadFormStepItemInner);
